import { useState, useMemo, useCallback, useEffect } from 'react'
import { useSocket } from '../../context/SocketContext'
import { 
  FiPlus, FiSearch, FiChevronDown, FiCalendar, FiClock, FiUser,
  FiFileText, FiEdit2, FiTrash2, FiChevronUp, FiAlertTriangle
} from 'react-icons/fi'
import './Tasks.css'

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:3001/api'

const Tasks = () => {
  const { tasks, agents, updateTask } = useSocket()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [priorityFilter, setPriorityFilter] = useState('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [expandedId, setExpandedId] = useState(null)
  const [editModal, setEditModal] = useState(null) // doc being edited
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [saveLoading, setSaveLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [docs, setDocs] = useState([])

  // Fetch document-type tasks from the docs API
  const fetchDocs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_BASE}/docs`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) throw new Error('Failed to fetch')
      const data = await res.json()
      setDocs(data)
    } catch (err) {
      console.error('Error fetching docs:', err)
    }
  }, [])

  useEffect(() => { fetchDocs() }, [fetchDocs])
  useEffect(() => { fetchDocs() }, [tasks])

  // Create task form state
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assigned_to: '',
    priority: 'p2',
    due_date: ''
  })

  // Edit form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigned_to: '',
    priority: 'p2',
    status: 'pending'
  })

  // Status configs
  const taskStatusConfig = {
    pending: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)', label: 'Pending' },
    in_progress: { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)', label: 'In Progress' },
    completed: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)', label: 'Completed' },
    blocked: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', label: 'Blocked' },
    cancelled: { color: '#64748b', bg: 'rgba(100, 116, 139, 0.1)', label: 'Cancelled' }
  }

  const docStatusConfig = {
    pending: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)', label: 'Pending' },
    approved: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)', label: 'Approved' },
    'needs-revision': { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', label: 'Needs Revision' },
    archived: { color: '#64748b', bg: 'rgba(100, 116, 139, 0.1)', label: 'Archived' },
    in_progress: { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)', label: 'In Progress' },
    completed: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)', label: 'Completed' },
    blocked: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', label: 'Blocked' },
    cancelled: { color: '#64748b', bg: 'rgba(100, 116, 139, 0.1)', label: 'Cancelled' }
  }

  const priorityConfig = {
    p0: { label: 'Critical', color: '#ef4444' },
    p1: { label: 'High', color: '#f59e0b' },
    p2: { label: 'Medium', color: '#3b82f6' },
    p3: { label: 'Low', color: '#64748b' }
  }

  // Combine docs + regular tasks. Docs come from their own API, tasks come from socket.
  const isDoc = (item) => {
    if (item.metadata && typeof item.metadata === 'string') {
      try { return JSON.parse(item.metadata).type === 'document' } catch {}
    }
    return false
  }

  // All items: both regular tasks and docs
  const allItems = useMemo(() => {
    // Get doc task IDs to filter them out of the regular tasks list
    const docIds = new Set(docs.map(d => d.task_id || d.id))
    const regularTasks = tasks.filter(t => !docIds.has(t.task_id || t.id))
    
    const combined = [
      ...docs.map(d => ({ ...d, _isDoc: true })),
      ...regularTasks.map(t => ({ ...t, _isDoc: false }))
    ]
    
    combined.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
    return combined
  }, [docs, tasks])

  const filteredItems = useMemo(() => {
    return allItems.filter(item => {
      const matchesSearch = item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.assigned_to?.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter
      const matchesPriority = priorityFilter === 'all' || item.priority === priorityFilter
      return matchesSearch && matchesStatus && matchesPriority
    })
  }, [allItems, searchTerm, statusFilter, priorityFilter])

  const handleStatusChange = (taskId, newStatus) => {
    updateTask(taskId, { status: newStatus })
  }

  const handleCreateTask = (e) => {
    e.preventDefault()
    if (!newTask.title || !newTask.assigned_to) return
    createTask({
      title: newTask.title,
      description: newTask.description,
      assigned_to: newTask.assigned_to,
      priority: newTask.priority,
      due_date: newTask.due_date || undefined
    })
    setShowCreateModal(false)
    setNewTask({ title: '', description: '', assigned_to: '', priority: 'p2', due_date: '' })
  }

  const getAgentName = (agentId) => {
    const agent = agents.find(a => a.agent_id === agentId)
    return agent?.name || agentId
  }

  const taskStats = useMemo(() => {
    return {
      total: allItems.length,
      pending: allItems.filter(t => t.status === 'pending').length,
      inProgress: allItems.filter(t => t.status === 'in_progress').length,
      completed: allItems.filter(t => t.status === 'completed').length,
      blocked: allItems.filter(t => t.status === 'blocked').length
    }
  }, [allItems])

  // === Doc-specific helpers ===

  const formatContent = (content) => {
    if (!content) return ''
    return content.replace(/\n{3,}/g, '\n\n').trim()
  }

  const getContentPreview = (content) => {
    if (!content) return ''
    const formatted = formatContent(content)
    if (formatted.length <= 200) return formatted
    const truncated = formatted.substring(0, 200)
    const lastSpace = truncated.lastIndexOf(' ')
    return (lastSpace > 0 ? truncated.substring(0, lastSpace) : truncated) + '...'
  }

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id)
  }

  const openEditModal = (doc) => {
    setEditModal(doc)
    setFormData({
      title: doc.title || '',
      description: doc.description || '',
      assigned_to: doc.assigned_to || '',
      priority: doc.priority || 'p2',
      status: doc.status || 'pending'
    })
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!formData.title || !formData.assigned_to) return
    setSaveLoading(true)
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_BASE}/docs/${editModal.task_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })
      if (!res.ok) throw new Error('Failed to update')
      const updated = await res.json()
      setDocs(prev => prev.map(d => (d.task_id || d.id) === (updated.task_id || updated.id) ? updated : d))
      setEditModal(null)
    } catch (err) {
      console.error('Save error:', err)
    } finally {
      setSaveLoading(false)
    }
  }

  const handleDelete = async (docId) => {
    setDeleteLoading(true)
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_BASE}/docs/${docId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) throw new Error('Failed to delete')
      setDocs(prev => prev.filter(d => (d.task_id || d.id) !== docId))
      setDeleteConfirm(null)
      setExpandedId(null)
    } catch (err) {
      console.error('Delete error:', err)
    } finally {
      setDeleteLoading(false)
    }
  }

  const quickStatusChange = (docId, newStatus) => {
    const update = async () => {
      try {
        const token = localStorage.getItem('token')
        const res = await fetch(`${API_BASE}/docs/${docId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: newStatus })
        })
        if (!res.ok) throw new Error('Failed to update')
        const updated = await res.json()
        setDocs(prev => prev.map(d => (d.task_id || d.id) === (updated.task_id || updated.id) ? updated : d))
      } catch (err) {
        console.error('Status update error:', err)
      }
    }
    update()
    updateTask(docId, { status: newStatus })
  }

  // Render a document card
  const renderDocCard = (doc) => {
    const docId = doc.task_id || doc.id
    const config = docStatusConfig[doc.status] || docStatusConfig.pending

    return (
      <div key={docId} className="doc-card">
        <div className="doc-card-header" onClick={() => toggleExpand(docId)}>
          <div className="doc-title-row">
            <div className="doc-title-main">
              <FiFileText className="doc-icon" />
              <h3>{doc.title}</h3>
            </div>
            <div className="doc-badges">
              <span className="doc-priority" style={{
                background: priorityConfig[doc.priority]?.bg || 'rgba(100,116,139,0.1)',
                color: priorityConfig[doc.priority]?.color || '#64748b'
              }}>
                {priorityConfig[doc.priority]?.label || 'Medium'}
              </span>
              <span className="doc-status-badge" style={{
                background: config.bg,
                color: config.color
              }}>
                {config.label}
              </span>
            </div>
          </div>
          <div className="doc-meta">
            <span className="doc-agent">
              <FiUser /> {getAgentName(doc.assigned_to)}
            </span>
            <span className="doc-date">
              <FiClock /> {new Date(doc.updated_at || doc.created_at).toLocaleDateString()}
            </span>
            <span className="doc-expand-icon">
              {expandedId === docId ? <FiChevronUp /> : <FiChevronDown />}
            </span>
          </div>
        </div>

        <p className="doc-preview">{getContentPreview(doc.description)}</p>

        {expandedId === docId && (
          <div className="doc-expanded">
            <div className="doc-content">
              {formatContent(doc.description).split('\n').map((line, i) => {
                if (line.startsWith('### ')) return <h4 key={i} className="doc-content-h4">{line.replace('### ', '')}</h4>
                if (line.startsWith('## ')) return <h3 key={i} className="doc-content-h3">{line.replace('## ', '')}</h3>
                if (line.startsWith('# ')) return <h2 key={i} className="doc-content-h2">{line.replace('# ', '')}</h2>
                if (line.startsWith('- ')) return <li key={i} className="doc-content-li">{line.replace('- ', '')}</li>
                if (line.startsWith('* ')) return <li key={i} className="doc-content-li">{line.replace('* ', '')}</li>
                if (line.match(/^\d+\.\s/)) return <li key={i} className="doc-content-li">{line}</li>
                if (line.trim() === '') return <div key={i} className="doc-content-spacer" />
                if (line.startsWith('|')) return <div key={i} className="doc-content-table">{line}</div>
                return <p key={i} className="doc-content-p">{line}</p>
              })}
            </div>

            <div className="doc-actions">
              <div className="doc-status-actions">
                {doc.status === 'pending' && (
                  <>
                    <button className="btn btn-sm btn-success" onClick={() => quickStatusChange(docId, 'approved')}>
                      Approve
                    </button>
                    <button className="btn btn-sm btn-warning" onClick={() => quickStatusChange(docId, 'needs-revision')}>
                      Request Revision
                    </button>
                  </>
                )}
                {doc.status === 'needs-revision' && (
                  <button className="btn btn-sm btn-primary" onClick={() => quickStatusChange(docId, 'pending')}>
                    Resubmit
                  </button>
                )}
              </div>
              <div className="doc-action-buttons">
                <button className="btn btn-sm btn-secondary" onClick={() => openEditModal(doc)}>
                  <FiEdit2 /> Edit
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => setDeleteConfirm(docId)}>
                  <FiTrash2 /> Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {deleteConfirm === docId && (
          <div className="doc-delete-confirm">
            <div className="delete-confirm-content">
              <FiAlertTriangle className="delete-icon" />
              <span>Are you sure? This will permanently delete this document.</span>
            </div>
            <div className="delete-confirm-actions">
              <button className="btn btn-sm btn-secondary" onClick={() => setDeleteConfirm(null)} disabled={deleteLoading}>Cancel</button>
              <button className="btn btn-sm btn-danger" onClick={() => handleDelete(docId)} disabled={deleteLoading}>
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Render a regular task card
  const renderTaskCard = (task) => (
    <div key={task.task_id || task.id} className="task-card">
      <div className="task-card-header">
        <div className="task-title-row">
          <h3>{task.title}</h3>
          <span className="task-priority" style={{
            background: priorityConfig[task.priority]?.bg || 'rgba(100,116,139,0.1)',
            color: priorityConfig[task.priority]?.color || '#64748b'
          }}>
            {priorityConfig[task.priority]?.label || 'Medium'}
          </span>
        </div>
        <div className="task-meta">
          <span className="task-agent">
            <FiUser /> {getAgentName(task.assigned_to)}
          </span>
          {task.due_date && (
            <span className="task-due">
              <FiCalendar /> {new Date(task.due_date).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      {task.description && (
        <p className="task-description">{task.description}</p>
      )}

      <div className="task-card-footer">
        <select
          value={task.status}
          onChange={(e) => handleStatusChange(task.task_id || task.id, e.target.value)}
          className="task-status-select"
          style={{
            background: taskStatusConfig[task.status]?.bg || 'rgba(100,116,139,0.1)',
            color: taskStatusConfig[task.status]?.color || '#64748b',
            borderColor: taskStatusConfig[task.status]?.color || '#64748b'
          }}
        >
          {Object.entries(taskStatusConfig).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.label}</option>
          ))}
        </select>
        <span className="task-date">
          <FiClock /> {new Date(task.created_at).toLocaleDateString()}
        </span>
      </div>
    </div>
  )

  return (
    <div className="tasks-page">
      <div className="tasks-header">
        <div>
          <h1>Tasks</h1>
          <p className="tasks-subtitle">
            {taskStats.inProgress} active · {taskStats.completed} completed · {docs.length} docs
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          <FiPlus /> New Task
        </button>
      </div>

      {/* Stats */}
      <div className="tasks-stats">
        <div className="tasks-stat">
          <span className="tasks-stat-value">{taskStats.total}</span>
          <span className="tasks-stat-label">Total</span>
        </div>
        <div className="tasks-stat">
          <span className="tasks-stat-value" style={{ color: 'var(--accent-warning)' }}>{taskStats.pending}</span>
          <span className="tasks-stat-label">Pending</span>
        </div>
        <div className="tasks-stat">
          <span className="tasks-stat-value" style={{ color: 'var(--accent-info)' }}>{taskStats.inProgress}</span>
          <span className="tasks-stat-label">In Progress</span>
        </div>
        <div className="tasks-stat">
          <span className="tasks-stat-value" style={{ color: 'var(--accent-success)' }}>{taskStats.completed}</span>
          <span className="tasks-stat-label">Completed</span>
        </div>
        <div className="tasks-stat">
          <span className="tasks-stat-value" style={{ color: 'var(--accent-danger)' }}>{taskStats.blocked}</span>
          <span className="tasks-stat-label">Blocked</span>
        </div>
      </div>

      {/* Filters */}
      <div className="tasks-filters">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search tasks and documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="approved">Approved</option>
          <option value="needs-revision">Needs Revision</option>
          <option value="blocked">Blocked</option>
          <option value="archived">Archived</option>
        </select>
        <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
          <option value="all">All Priority</option>
          <option value="p0">Critical</option>
          <option value="p1">High</option>
          <option value="p2">Medium</option>
          <option value="p3">Low</option>
        </select>
      </div>

      {/* Content list */}
      <div className="tasks-list">
        {filteredItems.length === 0 ? (
          <div className="tasks-empty">
            <div className="tasks-empty-icon">📋</div>
            <h3>No items found</h3>
            <p>Create a new task or adjust your filters</p>
          </div>
        ) : (
          filteredItems.map(item =>
            item._isDoc ? renderDocCard(item) : renderTaskCard(item)
          )
        )}
      </div>

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Task</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateTask}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Task Title *</label>
                  <input
                    type="text"
                    value={newTask.title}
                    onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter task title"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={newTask.description}
                    onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe the task..."
                    rows={3}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Assign To *</label>
                    <select
                      value={newTask.assigned_to}
                      onChange={(e) => setNewTask(prev => ({ ...prev, assigned_to: e.target.value }))}
                      required
                    >
                      <option value="">Select agent...</option>
                      {agents.map(agent => (
                        <option key={agent.agent_id} value={agent.agent_id}>
                          {agent.name} - {agent.role}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Priority</label>
                    <select
                      value={newTask.priority}
                      onChange={(e) => setNewTask(prev => ({ ...prev, priority: e.target.value }))}
                    >
                      <option value="p3">Low</option>
                      <option value="p2">Medium</option>
                      <option value="p1">High</option>
                      <option value="p0">Critical</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Due Date</label>
                  <input
                    type="date"
                    value={newTask.due_date}
                    onChange={(e) => setNewTask(prev => ({ ...prev, due_date: e.target.value }))}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={!newTask.title || !newTask.assigned_to}>
                  Create Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Document Modal */}
      {editModal && (
        <div className="modal-overlay" onClick={() => setEditModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Document</h2>
              <button className="modal-close" onClick={() => setEditModal(null)}>×</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Content</label>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={10}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Assign To *</label>
                    <select value={formData.assigned_to} onChange={e => setFormData(prev => ({ ...prev, assigned_to: e.target.value }))} required>
                      <option value="">Select agent...</option>
                      {agents.map(a => (
                        <option key={a.agent_id} value={a.agent_id}>{a.name} - {a.role}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Priority</label>
                    <select value={formData.priority} onChange={e => setFormData(prev => ({ ...prev, priority: e.target.value }))}>
                      <option value="p3">Low</option>
                      <option value="p2">Medium</option>
                      <option value="p1">High</option>
                      <option value="p0">Critical</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={formData.status} onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="needs-revision">Needs Revision</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setEditModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={!formData.title || !formData.assigned_to || saveLoading}>
                  {saveLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default Tasks
